import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Alert,
  Box,
  Skeleton,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  SvgIcon,
  Tooltip,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import AssetTable from "./AssetTable";
import ScopeSelector from "./ScopeSelector";

import {
  getPortfolioPowerQuery,
  getIsoListQuery,
  getUtilityListQuery,
  getResourceListQuery,
  getManufacturersListQuery,
} from "../../lib/Queries";

import Map, { Source, Layer, Popup } from "react-map-gl";

import MapPopup from "../Portfolio/MapPopup";

const Markets = () => {
  const accessToken =
    "pk.eyJ1Ijoibm1jbWlubiIsImEiOiJjbHMyOGJta2gwOWkyMm1sOHFkazl4aHZzIn0.aWlKbypBeiMOZl-g1GiUzw";

  const [activeDERTab, setActiveDERTab] = useState(0);
  const [activeScopeTab, setActiveScopeTab] = useState(0);
  const [totalSites, setTotalSites] = useState(0);
  const [totalCapacity, setTotalCapacity] = useState(0);
  const [totalSolar, setTotalSolar] = useState(0);
  const [totalBatteryKw, setTotalBatteryKw] = useState(0);
  const [totalBatteryKwh, setTotalBatteryKwh] = useState(0);
  const [totalGenset, setTotalGenset] = useState(0);
  const [lng] = useState(-99.2517);
  const [lat] = useState(40.168);
  const [popupSite, setPopupSite] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [zoom] = useState(3);
  const [entity, setEntity] = useState("all");
  const [scope, setScope] = useState("iso");
  const [resourceList, setResourceList] = useState([]);
  const [localData, setLocalData] = useState({ sites: [] });

  const [mapError, setMapError] = useState(false);
  const [unit, setUnit] = useState("MW");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mapCursor, setMapCursor] = useState("");
  const mapRef = useRef(null);

  const { data, isLoading: isPortfolioLoading, isError: isPortfolioError } = useQuery({
    ...getPortfolioPowerQuery(),
    refetchInterval: false,
  });

  const { data: isoListData } = useQuery({
    ...getIsoListQuery(),
  });

  const { data: utilityListData } = useQuery({
    ...getUtilityListQuery(true),
  });

  const { data: resourceListData } = useQuery({
    ...getResourceListQuery(entity, scope),
    enabled: Boolean(entity) && Boolean(scope),
  });

  const { data: manufacturersData } = useQuery({
    queryKey: ["manufacturers-list", "All"],
    ...getManufacturersListQuery("All"),
  });

  const manufacturerLookup = {};
  const modelLookup = {};
  if (manufacturersData) {
    manufacturersData.forEach((mfr) => {
      if (mfr.uuid) manufacturerLookup[mfr.uuid] = mfr.name;
      (mfr.model || []).forEach((m) => {
        if (m.uuid) modelLookup[m.uuid] = m.name;
      });
    });
  }

  useEffect(() => {
    let sites = data?.sites || [];
    setLocalData({ sites: sites });

    let filteredSites = sites.filter((site) => matchesStatusFilter(site.state));
    if (entity && entity !== "all") {
      filteredSites = filteredSites.filter((site) => {
        if (entity === "offgrid") {
          return scope === "iso" ? !site.iso?.uuid : !site.Utility?.uuid;
        }
        return scope === "iso"
          ? site.iso?.uuid === entity
          : site.Utility?.uuid === entity;
      });
    }
    setTotalSites(filteredSites.length);

    let capacity = 0;
    let solar = 0;
    let batteryKw = 0;
    let batteryKwh = 0;
    let genset = 0;
    filteredSites.forEach((site) => {
      solar += site.solar_capacity || 0;
      batteryKw += site.battery_capacity || 0;
      batteryKwh += site.battery_energy_capacity || 0;
      genset += site.genset_capacity || 0;
    });
    capacity = solar + batteryKw + genset;
    setTotalCapacity(Math.round(capacity));
    setTotalSolar(Math.round(solar));
    setTotalBatteryKw(Math.round(batteryKw));
    setTotalBatteryKwh(Math.round(batteryKwh));
    setTotalGenset(Math.round(genset));
  }, [data, statusFilter, entity, scope]);

  useEffect(() => {
    // create some arrays of synthetic BESS and Solar data
    let soc = [];
    let solar = [];
    for (let i = 0; i < 20; i++) {
      solar.push(Math.round(Math.random() * (95 - 80) + 80));
      soc.push(Math.round(Math.random() * (95 - 30) + 30));
    }
  }, []);

  useEffect(() => {
    if (resourceListData) {
      let updatedResourceList = { ...resourceListData };
      if (!updatedResourceList.Genset) {
        updatedResourceList.Genset = { array: [] };
      }
      if (!updatedResourceList.Solar) {
        updatedResourceList.Solar = { array: [] };
      }
      if (!updatedResourceList.BESS) {
        updatedResourceList.BESS = { array: [] };
      }

      if (statusFilter === "all") {
        setResourceList(updatedResourceList);
        return;
      }

      const sitesByUuid = {};
      (data?.sites || []).forEach((site) => {
        sitesByUuid[site.uuid] = site;
      });

      const filterByStatus = (arr) =>
        (arr || []).filter((r) => {
          if (!r) return false;
          const site = sitesByUuid[r.site_uuid];
          return matchesStatusFilter(site?.state);
        });

      setResourceList({
        Genset: { array: filterByStatus(updatedResourceList.Genset?.array) },
        Solar: { array: filterByStatus(updatedResourceList.Solar?.array) },
        BESS: { array: filterByStatus(updatedResourceList.BESS?.array) },
      });
    }
  }, [resourceListData, data, statusFilter]);

  const handleDERTabChange = (ev, val) => {
    setActiveDERTab(val);
  };

  const handleScopeTabChange = (ev, val) => {
    setActiveScopeTab(val);
  };

  const handleScopeSelection = (entity, scope) => {
    setEntity(entity);
    setScope(scope);
  };

  const showPopup = (site) => {
    setPopupSite(site);
    setPopupOpen(true);
  };

  const hidePopup = (ev) => {
    setPopupOpen(false);
  };

  const formatPower = (valueKw, suffix = "W") => {
    if (unit === "MW") {
      return `${(valueKw / 1000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} M${suffix}`;
    }
    return `${valueKw.toLocaleString()} k${suffix}`;
  };

  const handleUnitChange = (ev, newUnit) => {
    if (newUnit !== null) setUnit(newUnit);
  };

  const matchesStatusFilter = (siteState) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "operating") return siteState?.toLowerCase() === "operating";
    if (statusFilter === "in_build") return siteState?.toLowerCase() !== "operating";
    return true;
  };

  const sitesGeoJson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: (localData.sites || [])
        .filter(
          (s) =>
            s.longitude !== null &&
            s.longitude !== undefined &&
            s.latitude !== null &&
            s.latitude !== undefined,
        )
        .map((s) => ({
          type: "Feature",
          properties: {
            uuid: s.uuid,
            is_operating: s.state?.toLowerCase() === "operating" ? 1 : 0,
          },
          geometry: {
            type: "Point",
            coordinates: [s.longitude, s.latitude],
          },
        })),
    }),
    [localData.sites],
  );

  const clusterLayer = {
    id: "clusters",
    type: "circle",
    source: "sites",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": [
        "case",
        [">", ["get", "num_operating"], 0],
        "#85B953",
        "grey",
      ],
      "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26, 200, 32],
      "circle-stroke-width": 2,
      "circle-stroke-color": "#fff",
    },
  };

  const clusterCountLayer = {
    id: "cluster-count",
    type: "symbol",
    source: "sites",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
      "text-size": 13,
    },
    paint: {
      "text-color": "#fff",
    },
  };

  const unclusteredPointLayer = {
    id: "unclustered-point",
    type: "circle",
    source: "sites",
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-color": [
        "case",
        ["==", ["get", "is_operating"], 1],
        "#85B953",
        "grey",
      ],
      "circle-radius": 7,
      "circle-stroke-width": 2,
      "circle-stroke-color": "#fff",
    },
  };

  const handleMapClick = useCallback(
    (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      if (feature.layer.id === "clusters" || feature.layer.id === "cluster-count") {
        const clusterId = feature.properties.cluster_id;
        const source = mapRef.current?.getSource("sites");
        if (!source) return;
        source.getClusterExpansionZoom(clusterId, (err, zoomLevel) => {
          if (err) return;
          mapRef.current?.easeTo({
            center: feature.geometry.coordinates,
            zoom: zoomLevel,
            duration: 500,
          });
        });
      } else if (feature.layer.id === "unclustered-point") {
        const uuid = feature.properties.uuid;
        const site = (localData.sites || []).find((s) => s.uuid === uuid);
        if (site) showPopup(site);
      }
    },
    [localData.sites],
  );

  const getSelectedEntity = () => {
    let selectedEntity;
    if (entity && scope) {
      if (scope === "iso") {
        selectedEntity = isoListData?.iso.filter((iso) => iso.uuid === entity)[0];
      } else {
        selectedEntity = utilityListData.utilities.filter((utility) => utility.uuid === entity)[0];
      }
    }
    return selectedEntity;
  };

  const getLayers = () => {
    if (isoListData?.iso?.length > 0 && entity && scope) {
      const selectedEntity = getSelectedEntity();
      if (selectedEntity?.GeoJSON) {
        return (
          <Source id="iso" type="geojson" data={selectedEntity.GeoJSON}>
            <Layer
              id={"iso-layer"}
              type="line"
              source="iso"
              paint={{
                "line-color": "#325182",
                "line-width": 2,
              }}
            />
          </Source>
        );
      }
    }
  };

  return (
    <Box
      ml="80px"
      mt="30px"
      pl="50px"
      pr="50px"
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <Box mr="100px">
        <Typography className="dashboard" variant="h4">
          Scale Microgrids Portfolio
        </Typography>
      </Box>
      <Box>
        <div className="line"></div>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        mr="100px"
        height="450px"
        bgcolor="white"
        mb="30px"
        width="100%"
      >
        <Box display="flex" flexDirection="column" width="35%" p="20px" maxWidth="280px">
          <Tabs
            value={activeScopeTab}
            onChange={handleScopeTabChange}
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ "& .MuiTab-root.Mui-selected": { color: "secondary.main" } }}
          >
            <Tab label="ISO" />
            <Tab label="Utility" />
          </Tabs>
          {activeScopeTab === 0 ? (
            <Box overflow="scroll">
              <ScopeSelector
                selected={entity}
                list={isoListData?.iso}
                handleChange={(entity) => handleScopeSelection(entity, "iso")}
              />
            </Box>
          ) : null}
          {activeScopeTab === 1 ? (
            <Box overflow="scroll">
              <ScopeSelector
                selected={entity}
                list={utilityListData?.utilities}
                handleChange={(entity) => handleScopeSelection(entity, "utility")}
              />
            </Box>
          ) : null}
        </Box>
        <Box width="100%" height="100%" position="relative">
          {isPortfolioLoading ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : isPortfolioError || mapError ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Alert severity="error" sx={{ maxWidth: 420 }}>
                Unable to load the map. Please check your connection and try again.
              </Alert>
            </Box>
          ) : (
            <>
              <Map
                ref={mapRef}
                mapboxAccessToken={accessToken}
                initialViewState={{
                  latitude: lat,
                  longitude: lng,
                  zoom: zoom,
                }}
                mapStyle="mapbox://styles/mapbox/light-v9"
                onError={() => setMapError(true)}
                interactiveLayerIds={["clusters", "cluster-count", "unclustered-point"]}
                cursor={mapCursor}
                onMouseEnter={(e) => {
                  if (e.features && e.features.length > 0) setMapCursor("pointer");
                }}
                onMouseLeave={() => setMapCursor("")}
                onClick={handleMapClick}
              >
                <Source
                  id="sites"
                  type="geojson"
                  data={sitesGeoJson}
                  cluster={true}
                  clusterRadius={50}
                  clusterMaxZoom={10}
                  clusterProperties={{
                    num_operating: ["+", ["get", "is_operating"]],
                  }}
                >
                  <Layer {...clusterLayer} />
                  <Layer {...clusterCountLayer} />
                  <Layer {...unclusteredPointLayer} />
                </Source>
                {getLayers()}
                {popupOpen && (
                  <Popup
                    longitude={popupSite.longitude}
                    latitude={popupSite.latitude}
                    anchor="bottom"
                    onClose={(ev) => {
                      hidePopup(ev);
                    }}
                    closeOnClick={false}
                    style={{ maxWidth: "300px" }}
                  >
                    <MapPopup site={popupSite} />
                  </Popup>
                )}
              </Map>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 24,
                  right: 12,
                  zIndex: 1,
                  backgroundColor: "rgba(255, 255, 255, 0.92)",
                  borderRadius: "6px",
                  border: "1px solid #E1E2E4",
                  padding: "8px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "#85B953",
                      border: "2px solid #fff",
                      boxShadow: "0 0 0 1px #E1E2E4",
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Operational
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "grey",
                      border: "2px solid #fff",
                      boxShadow: "0 0 0 1px #E1E2E4",
                    }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    In Build
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box>
        <Typography variant="h4" color="secondary">
          {getSelectedEntity()?.name || ""}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mr="100px">
        <Tabs
          value={statusFilter}
          onChange={(ev, val) => setStatusFilter(val)}
          textColor="secondary"
          indicatorColor="secondary"
          sx={{ "& .MuiTab-root.Mui-selected": { color: "secondary.main" } }}
        >
          <Tab label="All" value="all" />
          <Tab label="Operating" value="operating" />
          <Tab label="In Build" value="in_build" />
        </Tabs>
        <Box display="flex" alignItems="center" gap="10px">
          <Typography variant="body2" color="textSecondary">
            Unit of Measure
          </Typography>
          <ToggleButtonGroup
            value={unit}
            exclusive
            onChange={handleUnitChange}
            size="small"
            color="secondary"
          >
            <ToggleButton value="MW" sx={{ textTransform: "none", px: 2 }}>
              MW
            </ToggleButton>
            <ToggleButton value="kW" sx={{ textTransform: "none", px: 2 }}>
              kW
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gap="24px"
        mb="30px"
      >
        <Box display="flex" alignItems="center" bgcolor="white" border="1px solid #E1E2E4" borderRadius="8px" p="16px 20px">
          <Box mr="16px" width="56px" height="56px" display="flex" justifyContent="center" alignItems="center" bgcolor="#F4F6FA" borderRadius="8px" flexShrink={0}>
            <SvgIcon color="secondary" sx={{ fontSize: 36 }}>
              <svg viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.0837 9.87023V2.14808C16.0837 1.91235 15.8594 1.74128 15.6321 1.80368L1.94229 5.56165C1.78719 5.60423 1.67969 5.74522 1.67969 5.90606V25.0469C1.67969 25.2441 1.83959 25.404 2.03683 25.404H7.0459M16.0837 9.87023H24.7644C24.9617 9.87023 25.1216 10.0301 25.1216 10.2274V25.0469C25.1216 25.2441 24.9617 25.404 24.7644 25.404H16.0837M16.0837 9.87023V25.404M16.0837 25.404H10.7175M10.7175 25.404V20.3949C10.7175 20.1977 10.5576 20.0378 10.3604 20.0378H7.40305C7.2058 20.0378 7.0459 20.1977 7.0459 20.3949V22.7209V25.404M10.7175 25.404H7.0459M5.91617 16.3662H11.5648M5.06888 12.6946H7.0459M5.06888 9.02293H7.0459M10.4351 9.02293H12.4121M12.4121 12.6946H10.4351M19.4729 15.2364H21.7324M21.7324 20.0378H19.4729" stroke="#325182" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </SvgIcon>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="4px">
              <Typography color="#B1B1B1" variant="subtitle2">Total Sites</Typography>
              <Tooltip placement="right" title="Total number of Scale sites within this region and status">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B1B1B1" }} />
              </Tooltip>
            </Box>
            <Typography variant="h5" color="secondary" fontWeight="600">{totalSites.toLocaleString()}</Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" bgcolor="white" border="1px solid #E1E2E4" borderRadius="8px" p="16px 20px">
          <Box mr="16px" width="56px" height="56px" display="flex" justifyContent="center" alignItems="center" bgcolor="#F4F6FA" borderRadius="8px" flexShrink={0}>
            <SvgIcon color="secondary" sx={{ fontSize: 36 }}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 17 A 9 9 0 0 1 21 17" stroke="#325182" strokeWidth="2" strokeLinecap="round" fill="none" />
                <path d="M6 17 L 7.2 14.5 M 12 8.5 L 12 11 M 18 17 L 16.8 14.5" stroke="#325182" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M12 17 L 17 10.5" stroke="#325182" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="17" r="1.6" fill="#325182" />
              </svg>
            </SvgIcon>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="4px">
              <Typography color="#B1B1B1" variant="subtitle2">Total kW Capacity</Typography>
              <Tooltip placement="right" title="Sum of Nameplate Solar, Battery and Genset Power in this region and status">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B1B1B1" }} />
              </Tooltip>
            </Box>
            <Typography variant="h5" color="secondary" fontWeight="600">{formatPower(totalCapacity)}</Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" bgcolor="white" border="1px solid #E1E2E4" borderRadius="8px" p="16px 20px">
          <Box mr="16px" width="56px" height="56px" display="flex" justifyContent="center" alignItems="center" bgcolor="#F4F6FA" borderRadius="8px" flexShrink={0}>
            <SvgIcon color="secondary" sx={{ fontSize: 36 }}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="6" width="18" height="12" rx="2" stroke="#325182" strokeWidth="2" />
                <path d="M22 10v4" stroke="#325182" strokeWidth="2" strokeLinecap="round" />
                <rect x="5" y="9" width="5" height="6" rx="0.5" fill="#325182" />
                <rect x="11.5" y="9" width="5" height="6" rx="0.5" fill="#325182" opacity="0.4" />
              </svg>
            </SvgIcon>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="4px">
              <Typography color="#B1B1B1" variant="subtitle2">Total Battery Capacity</Typography>
              <Tooltip placement="right" title="Sum of Nameplate Battery Capacity (kWh) in this region and status">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B1B1B1" }} />
              </Tooltip>
            </Box>
            <Typography variant="h5" color="secondary" fontWeight="600">{formatPower(totalBatteryKwh, "Wh")}</Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" bgcolor="white" border="1px solid #E1E2E4" borderRadius="8px" p="16px 20px">
          <Box mr="16px" width="56px" height="56px" display="flex" justifyContent="center" alignItems="center" bgcolor="#F4F6FA" borderRadius="8px" flexShrink={0}>
            <SvgIcon color="secondary" sx={{ fontSize: 36 }}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="5" stroke="#325182" strokeWidth="2" />
                <path d="M12 2V4M12 20V22M2 12H4M20 12H22M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M19.07 4.93L17.66 6.34M6.34 17.66L4.93 19.07" stroke="#325182" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </SvgIcon>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="4px">
              <Typography color="#B1B1B1" variant="subtitle2">Total Solar kW</Typography>
              <Tooltip placement="right" title="Sum of Nameplate Solar Power in this region and status">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B1B1B1" }} />
              </Tooltip>
            </Box>
            <Typography variant="h5" color="secondary" fontWeight="600">{formatPower(totalSolar)}</Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" bgcolor="white" border="1px solid #E1E2E4" borderRadius="8px" p="16px 20px">
          <Box mr="16px" width="56px" height="56px" display="flex" justifyContent="center" alignItems="center" bgcolor="#F4F6FA" borderRadius="8px" flexShrink={0}>
            <SvgIcon color="secondary" sx={{ fontSize: 36 }}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="7" width="14" height="11" rx="2" stroke="#325182" strokeWidth="2" />
                <circle cx="10" cy="12.5" r="3" stroke="#325182" strokeWidth="1.5" />
                <path d="M17 10H20C20.5523 10 21 10.4477 21 11V14C21 14.5523 20.5523 15 20 15H17" stroke="#325182" strokeWidth="2" />
                <path d="M7 7V5M13 7V5" stroke="#325182" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </SvgIcon>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="4px">
              <Typography color="#B1B1B1" variant="subtitle2">Total Genset kW</Typography>
              <Tooltip placement="right" title="Sum of Nameplate Genset Power in this region and status">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B1B1B1" }} />
              </Tooltip>
            </Box>
            <Typography variant="h5" color="secondary" fontWeight="600">{formatPower(totalGenset)}</Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" bgcolor="white" border="1px solid #E1E2E4" borderRadius="8px" p="16px 20px">
          <Box mr="16px" width="56px" height="56px" display="flex" justifyContent="center" alignItems="center" bgcolor="#F4F6FA" borderRadius="8px" flexShrink={0}>
            <SvgIcon color="secondary" sx={{ fontSize: 36 }}>
              <svg viewBox="0 0 23 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.72676 17.7394L16.2301 1.18586C16.5864 0.779129 17.245 1.14896 17.0833 1.66495L13.4251 13.3318C13.3241 13.6538 13.5647 13.9814 13.9022 13.9814H21.3286C21.7511 13.9814 21.9831 14.4728 21.7147 14.7991L8.63383 30.6976C8.28796 31.118 7.61449 30.7612 7.76801 30.2389L11.01 19.2099C11.1041 18.8897 10.864 18.5689 10.5303 18.5689H2.10283C1.6731 18.5689 1.44357 18.0627 1.72676 17.7394Z" stroke="#325182" strokeWidth="2" />
              </svg>
            </SvgIcon>
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap="4px">
              <Typography color="#B1B1B1" variant="subtitle2">Total Battery kW</Typography>
              <Tooltip placement="right" title="Sum of Nameplate Battery Power across all sites in this region and status">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#B1B1B1" }} />
              </Tooltip>
            </Box>
            <Typography variant="h5" color="secondary" fontWeight="600">{formatPower(totalBatteryKw)}</Typography>
          </Box>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" mb="30px">
        <Box>
          <Typography variant="h4" color="secondary">
            Assets Table
          </Typography>
        </Box>
        <Box>
          <div className="line"></div>
        </Box>
        <Box>
          <Tabs
            value={activeDERTab}
            onChange={handleDERTabChange}
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ "& .MuiTab-root.Mui-selected": { color: "secondary.main" } }}
          >
            <Tab label="BESS" />
            <Tab label="Solar" />
            <Tab label="Genset" />
          </Tabs>
        </Box>
        {activeDERTab === 0 ? (
          <Box>
            <AssetTable type="bess" list={resourceList?.BESS?.array || []} manufacturerLookup={manufacturerLookup} modelLookup={modelLookup} />
          </Box>
        ) : null}
        {activeDERTab === 1 ? (
          <Box>
            <AssetTable type="solar" list={resourceList?.Solar?.array || []} manufacturerLookup={manufacturerLookup} modelLookup={modelLookup} />
          </Box>
        ) : null}
        {activeDERTab === 2 ? (
          <Box>
            <AssetTable type="genset" list={resourceList?.Genset?.array || []} manufacturerLookup={manufacturerLookup} modelLookup={modelLookup} />
          </Box>
        ) : null}
      </Box>
      <Box height="40px"></Box>
    </Box>
  );
};

export default Markets;
